import { useEffect, useState } from 'react'
import { completarAnioFecha, fechaToISO, isoToFecha, maskFecha } from '../utils/fechas'

/**
 * Campo de fecha que SIEMPRE se ve DD/MM/AAAA.
 *
 * `<input type="date">` se dibuja con el idioma del navegador: en una máquina en
 * inglés muestra MM/DD/AAAA, y no hay atributo que lo cambie — es del navegador,
 * no del HTML. Con fechas de cobro eso no es cosmético: 06/08 y 08/06 son dos
 * días distintos y nadie se da cuenta hasta que el recibo sale mal.
 *
 * Hacia afuera habla en ISO (AAAA-MM-DD), igual que el campo nativo, así que
 * reemplazarlo no obliga a tocar quien lo usa.
 *
 * Se escribe libre y se completa con barras a medida que se tipea; lo que quedó
 * a medias se descarta al salir del campo en vez de guardar una fecha inventada.
 *
 * Día y mes solos (DD/MM + Enter, o salir del campo) completan el AÑO EN CURSO:
 * "0509" ⏎ → 05/09 del año actual. El Enter que completó la fecha no envía el
 * formulario — el próximo sí.
 */
export default function CampoFecha({ value, onChange, style = {}, ...props }) {
  const [texto, setTexto] = useState(() => isoToFecha(value) || '')

  // Si el valor cambia desde afuera (reset del formulario, autocompletado),
  // el texto acompaña. No se pisa mientras se está tipeando algo incompleto.
  useEffect(() => {
    const esperado = isoToFecha(value) || ''
    if (esperado !== texto && (texto === '' || fechaToISO(texto) !== value)) {
      setTexto(esperado)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const alTipear = (e) => {
    const t = maskFecha(e.target.value)
    setTexto(t)

    const iso = fechaToISO(t)
    if (iso) onChange(iso)
    else if (t === '') onChange('')
  }

  // DD/MM sin año → año en curso. Devuelve true si completó.
  const completarAnio = () => {
    const full = completarAnioFecha(texto)
    if (!full) return false
    setTexto(full)
    onChange(fechaToISO(full))
    return true
  }

  const alTecla = (e) => {
    if (e.key === 'Enter' && completarAnio()) e.preventDefault()
  }

  const alSalir = () => {
    if (completarAnio()) return
    // Media fecha no es una fecha: se limpia y se avisa visualmente al volver
    // al último valor bueno.
    if (texto !== '' && !fechaToISO(texto)) setTexto(isoToFecha(value) || '')
  }

  return (
    <input
      {...props}
      type="text"
      inputMode="numeric"
      placeholder="DD/MM/AAAA"
      maxLength={10}
      value={texto}
      onChange={alTipear}
      onKeyDown={alTecla}
      onBlur={alSalir}
      style={style}
    />
  )
}
